import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next'
import React from 'react'
import { Breadcrumbs } from '../components/Breadcrumbs'
import { CategoryTag } from '../components/CategoryTag'
import { Content } from '../components/Content'
import { HorizontalLine } from '../components/HorizontalLine'
import { Intro } from '../components/Intro'
import { MissingContent } from '../components/MissingContent'
import { Seo } from '../components/Seo'
import { Spacer } from '../components/Spacer'
import { WidthBound } from '../components/WidthBound'
import { usePageLink } from '../src/hooks/usePageLink'
import { ContentFragment, SeoFragment, SpecialPageType, TopicFragment, UUID } from '../src/model/fragments'
import { DEFAULT_LOCALE, getPossibleLocales, Locale } from '../src/model/locale'
import {
	getBlogArticleLink,
	getBlogCategoryLink,
	linkToAbsoluteUrl,
} from '../src/model/urls'
import { fetchGet } from '../src/utils/fetchWrapper'
import { useFormatDate } from '../src/utils/useFormatDate'

export default function BlogArticle({
	article,
	possibleLocales,
	locale,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
	const blogLink = usePageLink(SpecialPageType.blog)
	const formatDate = useFormatDate()

	if (!article) {
		return <MissingContent />
	}

	const category = article.categories.length ? article.categories[0] : undefined;

	let minutesMap : {[prop: string] : string} = {
		'1': 'minuta',
		'2': 'minuty',
		'3': 'minuty',
		'4': 'minuty',
		'default': 'minut'
	};

	if(locale == 'sk')
	{
		for(const k in minutesMap)
		{
			minutesMap[k] = minutesMap[k].replace('u', 'ú');
		}
	}

	return (
		<>
			<Seo seo={article.seo} />
			<Breadcrumbs
				items={[
					...(blogLink
						? [
								{
									label: 'Magazín', // @TODO: translate
									linkProps: blogLink,
								},
						  ]
						: []),
					...(category && category.link
						? [
								{
									label: category.name,
									linkProps: getBlogCategoryLink(
										locale,
										category.id,
										category.link.url,
									),
								},
						  ]
						: []),
					{
						label: article.title,
					},
				]}
			/>
			
			<WidthBound>
				<Intro
					title={article.title}
					subtitle={category ? <CategoryTag text={category.name} /> : undefined}
					additionalInfo={[
						formatDate(new Date(article.publishedAt)),
						article.readingTime ? `${article.readingTime} ${minutesMap[article.readingTime.toString()] ?? minutesMap['default']} čtení` : '',
					]}
				>
					<WidthBound size="narrow">{article.lead}</WidthBound>
				</Intro>
				
				<HorizontalLine />
				<Spacer initial={30} />
				<Content
					content={article.content}
					innerSize="narrow"
					share={
						article.link
							? {
									title: article.title,
									url: linkToAbsoluteUrl(
										getBlogArticleLink(locale, article.id, article.link.url),
									),
							  }
							: undefined
					}
				/>
			</WidthBound>
			<Spacer initial={130} />
		</>
	)
}

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
	const locale = (ctx.query.locale || DEFAULT_LOCALE) as Locale
	const possibleLocales = getPossibleLocales(locale)
	const { getBlogArticle: article } = await fetchGet('/blog/detail', new URLSearchParams({locale: locale, articleId: ctx.query.id?.toString() ?? ''})) as
	{
		getBlogArticle:  {
			id: UUID,
			link: {url: string},
			title: string,
			categories: {
				id: string,
				name: string,
				link: {url: string}
			}[],
			topics: TopicFragment[],
			publishedAt: Date,
			lead: string,
			readingTime: string,
			content: ContentFragment,
			seo: SeoFragment
		}
	};
			
	return { props: { article, possibleLocales, locale } }
}
